/**
 * Stub: expo-secure-store
 *
 * On Electron, delegates to the main process safeStorage IPC handler which
 * uses the OS keychain (macOS Keychain, Windows DPAPI, Linux libsecret).
 * On plain web, falls back to localStorage (not encrypted — acceptable for
 * session tokens on a trusted device).
 *
 * The public API mirrors `expo-secure-store`'s real signature, including the
 * trailing `options` arg (e.g. `{ keychainAccessible, requireAuthentication }`).
 * We ignore those options on web — they're keychain-level concepts the browser
 * doesn't expose. Accepting them keeps the stub a drop-in replacement so the
 * app code doesn't need any platform branching at the call sites.
 */

const isElectron = typeof window !== 'undefined' && !!window.electronBridge;

// Snapshot key material must NEVER ride the localStorage fallback — that
// is how the recovery mnemonic, account root key, and per-plan SMKs ended
// up plaintext in DevTools (they claim keychain semantics the browser
// cannot provide; see utilities/snapshotKeyPolicy.ts for the ruling).
// Electron is exempt: its path above is the OS keychain, not localStorage.
// Reads purge any legacy value before answering null, so machines that
// leaked before this shipped clean themselves on first touch.
const BROWSER_DENYLIST = /^snap_/;

export async function getItemAsync(key, _options) {
  if (isElectron) return window.electronBridge.secureStore.get(key);
  if (BROWSER_DENYLIST.test(key)) {
    try { localStorage.removeItem(key); } catch {}
    return null;
  }
  try { return localStorage.getItem(key); } catch { return null; }
}

export async function setItemAsync(key, value, _options) {
  if (isElectron) return window.electronBridge.secureStore.set(key, value);
  if (BROWSER_DENYLIST.test(key)) {
    // Silent-downgrade was the root defect; refuse loudly instead.
    console.warn(`[secure-store] refusing to write "${key}" to plaintext localStorage`);
    return;
  }
  try { localStorage.setItem(key, value); } catch {}
}

export async function deleteItemAsync(key, _options) {
  if (isElectron) return window.electronBridge.secureStore.delete(key);
  try { localStorage.removeItem(key); } catch {}
}

export async function isAvailableAsync() {
  return true; // localStorage is always available as fallback
}

export function canUseBiometricAuthentication() {
  // Real package returns true only when the OS reports a usable biometric
  // sensor. On web we have no biometric integration, so report `false` to
  // make callers fall back to non-auth code paths instead of attempting a
  // prompt that would never resolve.
  return false;
}

// iOS SecureStore accessibility constants — map to no-op values on web
export const WHEN_PASSCODE_SET_THIS_DEVICE_ONLY = 'WHEN_PASSCODE_SET_THIS_DEVICE_ONLY';
export const WHEN_UNLOCKED                       = 'WHEN_UNLOCKED';
export const WHEN_UNLOCKED_THIS_DEVICE_ONLY      = 'WHEN_UNLOCKED_THIS_DEVICE_ONLY';
export const AFTER_FIRST_UNLOCK                  = 'AFTER_FIRST_UNLOCK';
export const AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY = 'AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY';
export const ALWAYS                              = 'ALWAYS';
export const ALWAYS_THIS_DEVICE_ONLY             = 'ALWAYS_THIS_DEVICE_ONLY';

export default {
  getItemAsync, setItemAsync, deleteItemAsync, isAvailableAsync,
  canUseBiometricAuthentication,
  WHEN_PASSCODE_SET_THIS_DEVICE_ONLY, WHEN_UNLOCKED,
  WHEN_UNLOCKED_THIS_DEVICE_ONLY, AFTER_FIRST_UNLOCK,
  AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY,
  ALWAYS, ALWAYS_THIS_DEVICE_ONLY,
};
